import { Compass, Search } from 'lucide-react';
import { Link, useLocation } from 'react-router';
import { Button } from '../components/ui';
import { useOpenPalette } from '../features/search/command-palette';

/**
 * A page that does not exist.
 *
 * Unknown addresses used to redirect to the dashboard without a word, so a
 * mistyped link, an old bookmark or a Freshservice-era URL pasted into a chat
 * looked like the product had ignored the click. Saying so — and offering the
 * search, which is usually what the person wanted — costs nothing.
 *
 * Rendered inside the shell, so the navigation is right there; a signed-out
 * visitor is asked to sign in first and comes back to this answer.
 */
export function NotFoundRoute() {
  const location = useLocation();
  const openPalette = useOpenPalette();

  return (
    <div className="scrollbar-slim flex h-full items-center justify-center overflow-y-auto px-4 py-10 sm:px-6">
      <div className="max-w-md text-center">
        <Compass aria-hidden className="mx-auto mb-4 size-9 text-ink-subtle" />
        <h1 className="text-lg font-semibold tracking-tight text-ink">We couldn’t find that page</h1>
        <p className="mt-2 text-body text-ink-muted">
          Nothing lives at{' '}
          <code dir="ltr" className="rounded bg-sunken px-1.5 py-0.5 font-mono text-mini break-all text-ink">
            {location.pathname}
          </code>
          . The link may be mistyped, or what it pointed to may have moved.
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
          <Button variant="primary" onClick={openPalette}>
            <Search aria-hidden className="size-3.5" />
            Search for it
          </Button>
          <Button asChild>
            <Link to="/">Go to home</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
